import { useRef, useMemo /*useCallback*/, useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { usePopper } from 'react-popper';
import {
  Panel,
  ReactTable,
  IconButton,
  Popover,
  PopoverButton,
  StyledPopoverPanel,
  PopoverItem,
} from '@crazyegginc/hatch';

import { useFunnelEditorContext } from '../../funnel-editor-context';
import { round } from '/src/utils/math';
import { formatCurrency } from '/src/utils';
//import { STEP_TYPES } from '../../constants';
import { ROOT_ELEMENT_ID } from '/src/features/_global/constants';
//import { SORT_ORDER_TYPES } from '/src/features/_global/constants';

import { ReactComponent as MenuIcon } from '@crazyegginc/hatch/dist/images/icon-menu-dots.svg';
import { ReactComponent as MoneyIcon } from '@crazyegginc/hatch/dist/images/icon-currency-outline.svg';
import { ReactComponent as ExternalIcon } from '@crazyegginc/hatch/dist/images/icon-open-url-outline.svg';

export function FunnelResultTable({ loading, summary, totalValues, /*stepsData, goals,*/ currency }) {
  const { selectedStep } = useFunnelEditorContext();
  //const [field, setField] = useState('visitors');
  //const [sort, setSort] = useState(SORT_ORDER_TYPES.DESC);

  //const isEnterSiteStep = stepsData[selectedStep].type === STEP_TYPES.VISIT;
  //const goal = goals.find((g) => g.id === stepsData[selectedStep].id);

  const data = useMemo(() => {
    if (selectedStep >= summary.length) return []; // out-of-bounds

    const isLastStep = selectedStep === summary.length - 1;
    const current = summary[selectedStep];
    const next = summary[selectedStep + 1];
    const totalValue = totalValues[selectedStep];
    return [
      {
        audience: 'Everyone',
        visitors: current.toLocaleString(),
        conversions: isLastStep ? '-' : next.toLocaleString(),
        conversionRate: isLastStep ? '-' : `${round(current ? (next / current) * 100 : 0, 1)}%`,
        avgValue: formatCurrency(current ? totalValue / 10000 / current : 0, currency),
        totalValue: formatCurrency(totalValue / 10000, currency),
      },
    ];
  }, [summary, selectedStep, totalValues, currency]);

  const tableRef = useRef(null);
  const columns = useMemo(
    () => {
      const headerCellClassnames = '!normal-case !text-sm';
      const cols = [
        {
          header: <div>Audience</div>,
          accessorKey: 'audience',
          size: 80,
          cell: ({ row }) => (
            <>
              <span style={{ background: '#BA89F5' }} className="block w-1.25 h-full flex-shrink-0" />
              <div className="ml-3.75 font-semibold">{row.original.audience}</div>
            </>
          ),
          meta: {
            align: 'center',
            justify: 'left',
            padding: '0',
            headerCellClassnames,
          },
        },
        {
          header: 'Visitors',
          accessorKey: 'visitors',
          size: 30,
          cell: ({ row }) => <div className="">{row.original.visitors}</div>,
          meta: {
            align: 'center',
            justify: 'right',
            headerCellClassnames,
          },
        },
        {
          header: 'Conversions',
          accessorKey: 'conversions',
          size: 30,
          cell: ({ row }) => <div>{row.original.conversions}</div>,
          meta: {
            align: 'center',
            justify: 'right',
            headerCellClassnames,
          },
        },
        {
          header: <div className="text-right">Conversion Rate</div>,
          accessorKey: 'conversionRate',
          size: 30,
          cell: ({ row }) => <div>{row.original.conversionRate}</div>,
          meta: {
            align: 'center',
            justify: 'right',
            headerCellClassnames,
          },
        },
        /*{
        header: <div className="text-right">Avg Value ({currency})</div>,
        accessorKey: 'avgValue',
        size: 30,
        cell: ({ row }) => {
          if (isEnterSiteStep) {
            return (
              <div className="absolute left-0 -right-full text-body-5 pl-5 text-center">
                Cannot set Goal Value for ‘Enter Site’
              </div>
            );
          }
          if (goal?.hasValue) {
            return <div>{row.original.avgValue}</div>;
          }
          return (
            <Link
              to={`/goals/${goal.id}/edit-value`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-link text-right"
            >
              Set goal value
            </Link>
          );
        },
        meta: {
          align: 'center',
          justify: 'right',
          headerCellClassnames,
        },
      },
      {
        header: (
          <div
            className={classNames('text-right flex items-center space-x-0.5', {
              '-mr-4': !isEnterSiteStep,
            })}
          >
            <span>Total Value ({currency})</span>
            {isEnterSiteStep ? null : <GoalValueMenu id={goal?.id} />}
          </div>
        ),
        accessorKey: 'totalValue',
        size: 30,
        cell: ({ row }) => {
          if (isEnterSiteStep) {
            return null;
          }
          if (goal?.hasValue) {
            return <div className="">{row.original.totalValue}</div>;
          }
          return (
            <Link
              to={`/goals/${goal.id}/edit-value`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-link text-right"
            >
              Set goal value
            </Link>
          );
        },
        meta: {
          align: 'center',
          justify: 'right',
          headerCellClassnames,
        },
      },*/
      ];

      return cols;
    },
    [
      /*currency, isEnterSiteStep, goal*/
    ],
  );

  /*const tableSortData = useMemo(() => {
    return {
      id: field,
      desc: sort === SORT_ORDER_TYPES.DESC,
    };
  }, [field, sort]);

  const onFetchData = useCallback(
    ({ sorting }) => {
      if (sorting) {
        const nextField = sorting[0].id;
        setField(nextField);
        const nextSort = sorting[0].desc ? SORT_ORDER_TYPES.DESC : SORT_ORDER_TYPES.ASC;
        setSort(nextSort);
      }
    },
    [setField, setSort],
  );*/

  return (
    <Panel variant="secondary" className="!p-0 mt-5 overflow-hidden">
      <h2 className="mt-6 ml-5 text-header-2 mb-1">Summary</h2>
      <ReactTable
        fetching={loading}
        ref={{ tableRef }}
        rowPadding={false}
        columns={columns}
        data={data || []}
        enableSorting={false} // later for comparisons phase
        onFetchData={null} // later for comparisons phase
        sorting={null} // later for comparisons phase
        useVirtualization={false}
        className="!border-none !shadow-none"
        headerCustomClassname="mr-5 ml-3.75"
        rowCustomClassname={() => 'pr-5'}
      />
    </Panel>
  );
}

// eslint-disable-next-line
function GoalValueMenu({ id }) {
  const [referenceElement, setReferenceElement] = useState(null);
  const [tooltipElement, setTooltipElement] = useState(null);
  const { styles, attributes } = usePopper(referenceElement, tooltipElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: 'bottom',
          fallbackPlacements: 'bottom',
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
        },
      },
      useMemo(
        () => ({
          name: 'offset',
          options: {
            offset: ({ popper, reference }) => {
              return [-popper.width / 2 + reference.width / 2, 8];
            },
          },
        }),
        [],
      ),
    ],
  });

  return (
    <Popover className="relative">
      {({ open }) => (
        <div>
          <div ref={setReferenceElement}>
            <PopoverButton
              as={IconButton}
              className={classNames('text-cadet-blue-500 hover:text-dodger-blue-500 shrink-0', {
                'text-dodger-blue-500': open,
              })}
              icon={<MenuIcon className="h-4 w-4 fill-current" />}
              label="column menu"
            />
          </div>

          {open &&
            createPortal(
              <StyledPopoverPanel
                className="!w-48"
                ref={setTooltipElement}
                style={{ ...styles.popper }}
                {...attributes.popper}
              >
                {({ close }) => (
                  <>
                    <PopoverItem
                      component={Link}
                      to={`/goals/${id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        close();
                      }}
                      className="group/menu flex items-center"
                    >
                      <ExternalIcon className="mr-2.5 h-3.5 w-3.5 fill-current text-dodger-blue-300 group-hover/menu:text-dodger-blue-500" />
                      View Goal Details
                    </PopoverItem>
                    <PopoverItem
                      component={Link}
                      to={`/goals/${id}/edit-value`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        close();
                      }}
                      className="group/menu flex items-center"
                    >
                      <MoneyIcon className="mr-2.5 h-3.5 w-3.5 fill-current text-dodger-blue-300 group-hover/menu:text-dodger-blue-500" />
                      Set Goal Value
                    </PopoverItem>
                  </>
                )}
              </StyledPopoverPanel>,
              document.querySelector(`#${ROOT_ELEMENT_ID}`),
            )}
        </div>
      )}
    </Popover>
  );
}
